import { Link } from "react-router-dom";
import { BookOpen, Lightbulb, FileText, GitBranch, ArrowUpRight } from "lucide-react";
import MarketingLayout from "../../components/marketing/MarketingLayout";

const SECTIONS = [
  { id: "guides",  Icon: BookOpen,  title: "Guides",
    items: [
      { title: "Introducing Organizational Intelligence", tag: "Concept · 8 min read" },
      { title: "The Reality Gap: why leadership sees a different project than the team", tag: "Framework · 6 min read" },
      { title: "Non-accusatory recommendations: the design principle behind SYNQ", tag: "Design · 5 min read" },
    ]},
  { id: "insight", Icon: Lightbulb, title: "Intelligence Insights",
    items: [
      { title: "Assessment fatigue is a signal, not a nuisance", tag: "Insight" },
      { title: "Why experience-weighting must never change the score", tag: "Insight" },
      { title: "Anonymized theme clustering with LLMs — a privacy pattern", tag: "Insight" },
    ]},
  { id: "docs",    Icon: FileText,  title: "Docs",
    items: [
      { title: "Getting started with SYNQ Projects", tag: "Setup" },
      { title: "SYNQ Evolve — quick start", tag: "Setup" },
      { title: "Multi-tenant workspace primer", tag: "Advanced" },
    ]},
  { id: "change",  Icon: GitBranch, title: "Changelog",
    items: [
      { title: "Adaptive Organizational Intelligence layer shipped", tag: "This month" },
      { title: "Sprint Pulse (Scrum ceremony integration)", tag: "Recent" },
      { title: "Cross-domain auth + Forgot Password", tag: "Recent" },
    ]},
];

export default function ResourcesPage() {
  return (
    <MarketingLayout>
      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 -z-10">
          <div className="absolute -top-40 left-0 w-[700px] h-[700px] rounded-full bg-purple-100 dark:bg-purple-950/40 blur-3xl opacity-40" />
        </div>
        <div className="max-w-6xl mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-16">
          <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold mb-4">Resources</div>
          <h1 className="font-outfit text-5xl md:text-6xl font-black text-slate-900 dark:text-white leading-[1.05] max-w-4xl">
            The intelligence library.
          </h1>
          <p className="mt-6 max-w-2xl font-ibm-plex text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            Guides, essays, docs and shipping notes &mdash; everything we&rsquo;re learning while building SYNQ.
          </p>
        </div>
      </section>

      <section className="border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-20 space-y-14">
          {SECTIONS.map((s) => (
            <div key={s.id} id={s.id} className="scroll-mt-20" data-testid={`resource-${s.id}`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center">
                  <s.Icon className="w-5 h-5" />
                </div>
                <h2 className="font-outfit text-2xl md:text-3xl font-black text-slate-900 dark:text-white">{s.title}</h2>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {s.items.map((item, i) => (
                  <div key={i} className="group rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 hover:border-slate-900 dark:hover:border-white transition-colors cursor-pointer">
                    <div className="font-ibm-plex text-[10px] uppercase tracking-widest text-blue-600 dark:text-blue-400 font-bold mb-2">{item.tag}</div>
                    <div className="font-outfit font-bold text-slate-900 dark:text-white group-hover:underline">{item.title}</div>
                    <div className="mt-3 flex items-center gap-1 text-xs text-slate-500 group-hover:text-slate-900 dark:group-hover:text-white">
                      Read <ArrowUpRight className="w-3 h-3" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-20 text-center">
          <h2 className="font-outfit text-3xl md:text-4xl font-black text-slate-900 dark:text-white">Want to see SYNQ in action?</h2>
          <div className="mt-6">
            <Link to="/contact?intent=demo" className="inline-flex items-center gap-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-3.5 rounded-full font-outfit font-semibold text-[15px]">
              Request Demo <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
